import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { MODULE, ORGANIZATION_MODULE } from '../types/module.js';
import { normalizeTags } from '../utils/moduleTags.js';
import { normalizePlatforms } from '../utils/modulePlatforms.js';
/**
 * Coerce an id-list field (e.g. `GlobalConfig.defaultPlanIds`) to `string[]`.
 *
 * A bare string must never be passed to `for…of` / `.map` as an id list —
 * that iterates characters and creates one bogus entry per letter.
 * Strings are treated as a single id or a comma/whitespace-separated list.
 */
export function normalizeModuleIdList(value) {
    if (value == null)
        return [];
    if (Array.isArray(value)) {
        return value
            .filter((id) => typeof id === 'string')
            .map((id) => id.trim())
            .filter((id) => id.length > 0);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return [];
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed))
                    return normalizeModuleIdList(parsed);
            }
            catch {
                // fall through to delimiter split
            }
        }
        return trimmed
            .split(/[,\s]+/)
            .map((id) => id.trim())
            .filter((id) => id.length > 0);
    }
    return [];
}
class ModuleService {
    async getRepository() {
        return await repositoryFactory.getRepository('module', MODULE, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    /**
     * Get a single module by ID
     */
    async getModuleById(moduleId) {
        try {
            const repository = await this.getRepository();
            return await repository.getById(moduleId);
        }
        catch (error) {
            rtLogService.error('Error fetching module', { error, moduleId });
            return null;
        }
    }
    /**
     * Get multiple modules by their IDs
     */
    async getModulesByIds(moduleIds) {
        if (moduleIds.length === 0) {
            return [];
        }
        try {
            const repository = await this.getRepository();
            // Fetch all modules and filter by IDs (Firestore 'in' operator has a limit of 10 items)
            // For better performance with large arrays, we could batch the requests
            if (moduleIds.length <= 10) {
                const modules = await repository.getAll({
                    filters: [{ field: '__name__', operator: 'in', value: moduleIds }]
                });
                return modules;
            }
            else {
                // Batch requests for more than 10 IDs
                const batches = [];
                for (let i = 0; i < moduleIds.length; i += 10) {
                    batches.push(moduleIds.slice(i, i + 10));
                }
                const results = await Promise.all(batches.map(batch => repository.getAll({
                    filters: [{ field: '__name__', operator: 'in', value: batch }]
                })));
                return results.flat();
            }
        }
        catch (error) {
            rtLogService.error('Error fetching modules by IDs', { error, moduleIds });
            return [];
        }
    }
    /**
     * Get all modules
     */
    async getAllModules() {
        try {
            const repository = await this.getRepository();
            return await repository.getAll();
        }
        catch (error) {
            rtLogService.error('Error fetching all modules', { error });
            return [];
        }
    }
    /**
     * Finds the global Module whose actionProperties.redirectTo equals the given key.
     * Used by the module config system to link manifest keys to Module documents.
     * The full module list is small and cached (CacheStrategy.InMemory), so a scan is fine.
     */
    async getModuleByRedirectTo(redirectTo) {
        try {
            const all = await this.getAllModules();
            return all.find(m => m.actionProperties?.redirectTo === redirectTo) ?? null;
        }
        catch (error) {
            rtLogService.error('Error finding module by redirectTo', { error, redirectTo });
            return null;
        }
    }
    /**
     * Returns all OrganizationModule docs that link a specific base module to an org.
     * Used by the module config system to find which OrgModule to read params from.
     */
    async getOrgModulesByModuleId(orgId, moduleId) {
        try {
            const orgModuleRepo = await repositoryFactory.getRepository(`org/${orgId}/modules`, ORGANIZATION_MODULE, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            const all = await orgModuleRepo.getAll();
            return all.filter(om => om.moduleId === moduleId);
        }
        catch (error) {
            rtLogService.error('Error fetching org modules by moduleId', { error, orgId, moduleId });
            return [];
        }
    }
    /**
     * Adds a module to an organization.
     * Creates a record in org/<orgId>/modules with auto-generated ID.
     * @param orgId - The organization ID
     * @param moduleId - The base module ID to link
     * @returns The created OrganizationModule
     */
    async addModuleToOrganization(orgId, moduleId) {
        try {
            const orgModuleRepo = await repositoryFactory.getRepository(`org/${orgId}/modules`, ORGANIZATION_MODULE, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            const orgModule = await orgModuleRepo.create({ moduleId });
            rtLogService.info('Module added to organization', { orgId, moduleId, orgModuleId: orgModule.id });
            return orgModule;
        }
        catch (error) {
            rtLogService.error('Error adding module to organization', { orgId, moduleId, error });
            throw error;
        }
    }
    /**
     * Adds multiple modules to an organization.
     * @param orgId - The organization ID
     * @param moduleIds - Base module IDs to link (array preferred; strings are normalized)
     * @returns Array of created OrganizationModule records
     */
    async addModulesToOrganization(orgId, moduleIds) {
        const ids = normalizeModuleIdList(moduleIds);
        if (typeof moduleIds === 'string' && moduleIds.length > 0) {
            rtLogService.warn('addModulesToOrganization received a string; coerced to id list (avoid for…of on strings)', { orgId, raw: moduleIds, ids });
        }
        const results = [];
        for (const moduleId of ids) {
            const orgModule = await this.addModuleToOrganization(orgId, moduleId);
            results.push(orgModule);
        }
        return results;
    }
    /**
     * Resolve profile modules by merging OrganizationModule overrides with base Module data.
     * Accepts repositories as parameters to work with repositoryResolver.
     */
    async resolveProfileModules(orgModulesRepo, moduleRepo, profileModuleIds) {
        if (profileModuleIds.length === 0)
            return [];
        try {
            const allOrgModules = await orgModulesRepo.getAll();
            const profileOrgModules = allOrgModules.filter(m => profileModuleIds.includes(m.id));
            // Batch-fetch base modules
            const uniqueBaseIds = [...new Set(profileOrgModules.map(m => m.moduleId))];
            const baseModules = await this.fetchModulesByIds(moduleRepo, uniqueBaseIds);
            const baseModulesMap = new Map(baseModules.map(m => [m.id, m]));
            return profileOrgModules.map(orgModule => {
                const baseModule = baseModulesMap.get(orgModule.moduleId);
                return {
                    id: orgModule.id,
                    name: orgModule.name ?? baseModule?.name ?? orgModule.moduleId,
                    description: orgModule.description ?? baseModule?.description ?? '',
                    action: orgModule.action ?? baseModule?.action ?? 'navigate',
                    actionProperties: orgModule.actionProperties ?? baseModule?.actionProperties ?? {},
                    icon: orgModule.icon ?? baseModule?.icon,
                };
            });
        }
        catch (error) {
            rtLogService.error('Error resolving profile modules', { error });
            return [];
        }
    }
    /**
     * Creates a new module document.
     * The caller is responsible for generating the ID (crypto.randomUUID) or
     * leaving it empty to let the repository assign one.
     */
    async createModule(data) {
        try {
            const repository = await this.getRepository();
            const platforms = 'platforms' in data ? normalizePlatforms(data.platforms) : undefined;
            const payload = {
                ...data,
                ...(data.tags != null ? { tags: normalizeTags(data.tags) } : {}),
            };
            if (platforms !== undefined) {
                payload.platforms = platforms;
            }
            else {
                delete payload.platforms;
            }
            const created = await repository.create(payload);
            rtLogService.info('Module created', { moduleId: created.id, name: data.name });
            return created;
        }
        catch (error) {
            rtLogService.error('Error creating module', { error, name: data.name });
            throw error;
        }
    }
    /**
     * Updates an existing module document. Pass only the fields you want to change.
     */
    async updateModule(moduleId, data) {
        try {
            const repository = await this.getRepository();
            const payload = data.tags != null ? { ...data, tags: normalizeTags(data.tags) } : { ...data };
            if ('platforms' in data) {
                const platforms = normalizePlatforms(data.platforms);
                if (platforms !== undefined) {
                    payload.platforms = platforms;
                }
                else {
                    // Collapse empty/invalid to absent (read default = console).
                    delete payload.platforms;
                }
            }
            await repository.update(moduleId, payload);
            rtLogService.info('Module updated', { moduleId });
        }
        catch (error) {
            rtLogService.error('Error updating module', { moduleId, error });
            throw error;
        }
    }
    /**
     * Deletes a module document. Does NOT clean up OrganizationModule links —
     * that orchestration belongs to the caller.
     */
    async deleteModule(moduleId) {
        try {
            const repository = await this.getRepository();
            await repository.delete(moduleId);
            rtLogService.info('Module deleted', { moduleId });
        }
        catch (error) {
            rtLogService.error('Error deleting module', { moduleId, error });
            throw error;
        }
    }
    /**
     * Fetch modules by IDs using a provided repository.
     * Handles Firestore 'in' operator limit of 10.
     */
    async fetchModulesByIds(moduleRepo, moduleIds) {
        if (moduleIds.length === 0)
            return [];
        try {
            if (moduleIds.length <= 10) {
                return await moduleRepo.getAll({
                    filters: [{ field: '__name__', operator: 'in', value: moduleIds }]
                });
            }
            const batches = [];
            for (let i = 0; i < moduleIds.length; i += 10) {
                batches.push(moduleIds.slice(i, i + 10));
            }
            const results = await Promise.all(batches.map(batch => moduleRepo.getAll({
                filters: [{ field: '__name__', operator: 'in', value: batch }]
            })));
            return results.flat();
        }
        catch (error) {
            rtLogService.error('Error fetching modules by IDs', { error, moduleIds });
            return [];
        }
    }
}
export const moduleService = new ModuleService();
//# sourceMappingURL=moduleService.js.map